import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Avatar,
  Collapse,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Box,
  useTheme,
  Divider,
  IconButton,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useAccountTransactionStatus, useUserTransactionStatus, useMe } from 'src/hooks'

import { ImpactRecord } from '../scans-table/collapsed/impact-record.component'
import { ScanMap } from '../scans-table/collapsed/map.component'
import { MediaList } from '../scans-table/collapsed/media.component'
import { OuterLink } from '../scans-table/collapsed/outer-link.component'
import { Loader } from 'src/components/loader'

import {
  capitalizeFirstLetter,
  createProductQRLink,
  findLocationsWithinGeofence,
  getBoundsIncludingRadii,
  getScanExplorerTxUrl,
  getTableTimestamp,
} from 'src/common/utils'
import { COORDS_MULTIPLIER, WS_TRANSACTION_STATUS } from 'src/constants'
import { EScanStatus, IStepLocation } from 'src/types'
import { ETranslationNs, IScanRespDto } from 'src/types'

import { useStyles } from './styles'
import { ExternalLink } from 'src/assets/icons/v2/ExternalLink'
import { DEFAULT_LANGUAGE_CODE } from 'src/components/languages/langauges-list/constants'

interface IGridItemCollapseProps {
  open: boolean
  handleClick?: () => void
  item: IScanRespDto
  isScaner: boolean
}

const GridItemCollapse = ({ open, item, isScaner }: IGridItemCollapseProps) => {
  const [t, i18n] = useTranslation([
    ETranslationNs.Form,
    ETranslationNs.Scans,
    ETranslationNs.ScanForm,
  ])
  const classes = useStyles()
  const theme = useTheme()
  const { data: user } = useMe()
  // const { mutateAsync: updateReviewed, isLoading: isUpdatingReviewed } = useUpdateScanStatus()
  // const { mutateAsync: pushScanToBc, isLoading: isPushing } = usePushScanToBc()

  const mapRef = useRef<google.maps.Map | null>(null)

  const userTransaction = useUserTransactionStatus(item.id)
  const [userTxLoader, setUserTxLoader] = useState(
    userTransaction.status === WS_TRANSACTION_STATUS.PENDING,
  )

  const accountTransaction = useAccountTransactionStatus(item.id)
  const [accountTxLoader, setAccountTxLoader] = useState(
    accountTransaction.status === WS_TRANSACTION_STATUS.PENDING,
  )

  useEffect(() => {
    if (userTransaction.status === WS_TRANSACTION_STATUS.PENDING) {
      setUserTxLoader(true)
    } else {
      setUserTxLoader(false)
    }
  }, [userTransaction.status])
  useEffect(() => {
    if (accountTransaction.status === WS_TRANSACTION_STATUS.PENDING) {
      setAccountTxLoader(true)
    } else {
      setAccountTxLoader(false)
    }
  }, [accountTransaction.status])

  // const handlePushToBc = () => {
  //   pushScanToBc(item.id)
  // }

  // const handleReviewedUpdate = async () => {
  //   await updateReviewed({ id: item.id, status: EScanStatus.APPROVED, accountId: item.account?.id })
  // }

  const handlOpen = (link: string) => {
    window.open(link, '_blank')
  }

  const stepConnectedAccount = item?.step?.account_connection

  const stepLocations: IStepLocation[] = useMemo(() => {
    return item.step?.locations
      ? item.step.locations.map((location) => ({
          lat: location.latitude / COORDS_MULTIPLIER,
          lng: location.longitude / COORDS_MULTIPLIER,
          radius: location.radius,
          name: item.step?.name,
          location_name: location.location_name,
          connected_account_name: stepConnectedAccount?.account_approved
            ? stepConnectedAccount?.account?.account_holder
            : null,
          connected_account_img:
            stepConnectedAccount?.account.image_small ||
            stepConnectedAccount?.account.image_medium ||
            null,
        }))
      : []
  }, [item.step?.locations, stepConnectedAccount])

  const scannerLocation = useMemo(
    () => ({
      lat: item.scanner_latitude / COORDS_MULTIPLIER,
      lng: item.scanner_longitude / COORDS_MULTIPLIER,
    }),
    [item.scanner_latitude, item.scanner_longitude],
  )

  const center = useMemo(() => {
    const allLocations = [
      scannerLocation,
      ...stepLocations.map((loc) => ({ lat: loc.lat, lng: loc.lng })),
    ]

    const latitudes = allLocations.map((loc) => loc.lat)
    const longitudes = allLocations.map((loc) => loc.lng)

    const minLat = Math.min(...latitudes)
    const maxLat = Math.max(...latitudes)
    const minLng = Math.min(...longitudes)
    const maxLng = Math.max(...longitudes)

    return {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2,
    }
  }, [scannerLocation, stepLocations])

  const handleCenter = useCallback(() => {
    if (!mapRef?.current) return

    const locationsWithinGeofence = findLocationsWithinGeofence(stepLocations, scannerLocation)

    const locationsWithRadius = locationsWithinGeofence.map((step) => ({
      lat: step.lat,
      lng: step.lng,
      radius: step.radius || 0,
    }))

    const bounds = getBoundsIncludingRadii(locationsWithRadius)

    mapRef.current.fitBounds(bounds)

    const zoomLevelAdjustment = 1
    const map = mapRef.current

    if (map) {
      google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        const currentZoom = map.getZoom()
        if (currentZoom) {
          map.setZoom(currentZoom + zoomLevelAdjustment)
        }
      })
    }
  }, [item.scanner_latitude, item.scanner_longitude, item.step_radius, stepLocations])

  return (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <List component="div" className={classes.collapseItemsWrapper}>
        <ListItem disablePadding>
          {item.account && (
            <ListItemAvatar>
              <Avatar
                src={item.account.image_small || item.account.image}
                alt={item.account.account_holder}
              />
            </ListItemAvatar>
          )}
          <ListItemText
            primaryTypographyProps={{ style: { fontSize: '14px' } }}
            primary={item.account?.account_holder || item.account_name}
          />
        </ListItem>
        {item.product && (
          <ListItem disablePadding>
            <ListItemAvatar>
              <Avatar
                src={item.product.image_small || item.product.image}
                alt={item.product.name}
              />
            </ListItemAvatar>

            <ListItemText
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              primary={item.product?.name || item.product_name}
              secondary={
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    justifyContent: 'start',
                    gap: '5px',
                    color: theme.palette.custom.grey3,
                    fontSize: '9px',
                  }}
                >
                  {t('scan-history-journey-page-text.v')}
                  <IconButton
                    sx={{
                      padding: '2px',
                    }}
                    onClick={() => {
                      handlOpen(
                        createProductQRLink({
                          productId: item.product?.simple_id || '',
                          lng: i18n.language || DEFAULT_LANGUAGE_CODE,
                        }),
                      )
                    }}
                  >
                    <ExternalLink size={12} stroke={theme.palette.custom.grey2} />
                  </IconButton>
                </Box>
              }
            />
          </ListItem>
        )}
        {item.step && (
          <ListItem disablePadding>
            <ListItemAvatar>
              <Avatar src={item.step.image_small || item.step.image} alt={item.step.name} />
            </ListItemAvatar>

            <ListItemText
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              primary={item.step?.name || item.step_name}
            />
          </ListItem>
        )}
        {item?.batch_id && (
          <ListItem disablePadding>
            <ListItemText
              primary={t('batch-id-table-column.v', { ns: ETranslationNs.Scans }) + ':'}
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              secondary={item.batch_id}
            />
          </ListItem>
        )}
        <ListItem disablePadding>
          <ListItemText
            primary={t('scan-form-record-type-label.v', { ns: ETranslationNs.ScanForm }) + ':'}
            primaryTypographyProps={{ style: { fontSize: '14px' } }}
            secondary={capitalizeFirstLetter(item.record_type)}
          />
        </ListItem>

        <ListItem disablePadding>
          <ListItemText
            primary={t('table-created-column.v', { ns: ETranslationNs.Common }) + ':'}
            primaryTypographyProps={{ style: { fontSize: '14px' } }}
            secondary={getTableTimestamp({ date: item.created_at, timezone: user?.timezone })}
          />
        </ListItem>

        {item?.sender_reference_id && (
          <ListItem disablePadding>
            <ListItemText
              primary={
                t('scan-form-s-reference-input-label.v', { ns: ETranslationNs.ScanForm }) + ':'
              }
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              secondary={item.sender_reference_id}
            />
          </ListItem>
        )}
        {item?.receiver_reference_id && (
          <ListItem disablePadding>
            <ListItemText
              primary={
                t('scan-form-r-reference-input-label.v', { ns: ETranslationNs.ScanForm }) + ':'
              }
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              secondary={item.receiver_reference_id}
            />
          </ListItem>
        )}
        {item?.invoice_reference && (
          <ListItem disablePadding>
            <ListItemText
              primary={
                t('scan-form-invoice-reference-input-label.v', { ns: ETranslationNs.ScanForm }) +
                ':'
              }
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              secondary={item.invoice_reference}
            />
          </ListItem>
        )}
        {item.impact && (
          <ListItem disablePadding>
            <ListItemText
              primary={t('scan-form-impact-checkbox.v', { ns: ETranslationNs.ScanForm }) + ':'}
              primaryTypographyProps={{ style: { fontSize: '14px', marginBottom: '8px' } }}
              secondary={
                <ImpactRecord
                  impact={item.impact}
                  quantity={item.impact_quantity}
                  quantity_type={item.impact_quantity_type}
                />
              }
            />
          </ListItem>
        )}
        {item?.note && (
          <ListItem disablePadding>
            <ListItemText
              primary={t('scan-note-input-label.v', { ns: ETranslationNs.ScanForm }) + ':'}
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              secondary={item.note}
            />
          </ListItem>
        )}
        {item?.media && item.media.length > 0 && (
          <ListItem disablePadding>
            <ListItemText
              primary={t('scan-media-input-title.v', { ns: ETranslationNs.Scans }) + ':'}
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              secondary={<MediaList media={item.media} />}
            />
          </ListItem>
        )}
        <ListItem disablePadding>
          <ListItemText
            primary={t('within-geofence-label.v', { ns: ETranslationNs.Scans }) + ':'}
            primaryTypographyProps={{ style: { fontSize: '14px' } }}
            secondaryTypographyProps={{ style: { fontSize: '14px' } }}
            secondary={
              item.within_geofence ? (
                <Link
                  style={{ color: theme.palette.custom.primary }}
                  to="#"
                  onClick={(e) => {
                    e.preventDefault()
                    handleCenter()
                  }}
                >
                  {t('scan-yes-label.v', { ns: ETranslationNs.Scans })}
                </Link>
              ) : (
                t('scan-no-label.v', { ns: ETranslationNs.Scans })
              )
            }
          />
        </ListItem>
        <ListItem disablePadding>
          <ScanMap
            scannerLocation={scannerLocation}
            center={center}
            radius={item.step_radius}
            scannerName={item.scanner_name}
            stepName={item.step_name}
            stepLocations={stepLocations}
            mapRef={mapRef}
          />
        </ListItem>

        <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px' }} />
        <ListItem disablePadding>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {t('user-tx-hash-label.v', { ns: ETranslationNs.Scans }) + ':'}
                {(userTxLoader || !item.user_tx) && <Loader size={18} isStatic />}
              </Box>
            }
            primaryTypographyProps={{ style: { fontSize: '14px' } }}
            secondary={
              !userTxLoader || item.user_tx ? (
                <OuterLink
                  to={item.user_tx && getScanExplorerTxUrl(item.user_tx, item.network)}
                  text={userTransaction.txHash || item.user_tx || '-'}
                />
              ) : (
                ''
              )
            }
          />
        </ListItem>
        <ListItem disablePadding>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {t('account-tx-hash-label.v', { ns: ETranslationNs.Scans }) + ':'}
                {accountTxLoader && <Loader size={18} isStatic />}
              </Box>
            }
            primaryTypographyProps={{ style: { fontSize: '14px' } }}
            secondary={
              !accountTxLoader ? (
                <OuterLink
                  to={item.account_tx && getScanExplorerTxUrl(item.account_tx, item.network)}
                  text={accountTransaction.txHash || item.account_tx || '-'}
                />
              ) : (
                ''
              )
            }
          />
        </ListItem>
        {(item?.reviewed_by ||
          item?.reviewed_at ||
          item?.pushed_at ||
          item?.pushed_by ||
          item?.not_approved_by ||
          item?.not_approved_on) &&
          !isScaner && <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px' }} />}

        {item?.reviewed_by && (
          <ListItem disablePadding>
            <ListItemText
              primary={t('scan-history-approved-by-lable.v', { ns: ETranslationNs.Scans }) + ':'}
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              secondary={item.reviewed_by}
            />
          </ListItem>
        )}
        {item?.reviewed_at && (
          <ListItem disablePadding>
            <ListItemText
              primary={t('table-reviewed-at-column.v', { ns: ETranslationNs.Scans }) + ':'}
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              secondary={getTableTimestamp({ date: item.reviewed_at, timezone: user?.timezone })}
            />
          </ListItem>
        )}
        {item?.not_approved_by && (
          <ListItem disablePadding>
            <ListItemText
              primary={t('scan-not-approved-by-lable.v', { ns: ETranslationNs.Scans }) + ':'}
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              secondary={item.not_approved_by}
            />
          </ListItem>
        )}
        {item?.not_approved_on && (
          <ListItem disablePadding>
            <ListItemText
              primary={t('scan-not-approved-at-lable.v', { ns: ETranslationNs.Scans }) + ':'}
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              secondary={getTableTimestamp({
                date: item.not_approved_on,
                timezone: user?.timezone,
              })}
            />
          </ListItem>
        )}
        {item?.pushed_by && (
          <ListItem disablePadding>
            <ListItemText
              primary={t('scan-history-pushed-by-lable.v', { ns: ETranslationNs.Scans }) + ':'}
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              secondary={item.pushed_by}
            />
          </ListItem>
        )}
        {item?.pushed_at && (
          <ListItem disablePadding>
            <ListItemText
              primary={t('scan-history-pushed-at-lable.v', { ns: ETranslationNs.Scans }) + ':'}
              primaryTypographyProps={{ style: { fontSize: '14px' } }}
              secondary={getTableTimestamp({ date: item.pushed_at, timezone: user?.timezone })}
            />
          </ListItem>
        )}
        {/* <ListItem disablePadding>
          <FormControlLabel
            control={
              <Switch
                size="small"
                disabled={
                  isUpdatingReviewed ||
                  item.account_tx_pushed ||
                  isPushing ||
                  item?.account_role_for_user === EScanAccountUserRoles.USER ||
                  item?.account_role_for_user === EScanAccountUserRoles.SCANNER
                }
                onClick={handleReviewedUpdate}
                checked={item.reviewed}
              />
            }
            labelPlacement="start"
            label={t('reviewed-table-column.v', { ns: ETranslationNs.Scans })}
            sx={{
              marginLeft: 0,

              '& .MuiFormControlLabel-label': { marginRight: '8px', fontSize: 14 },
            }}
          />
        </ListItem> */}
        {/* <ListItem>
          <Button
            sx={{ fontSize: 14 }}
            onClick={handlePushToBc}
            disabled={
              !item.reviewed ||
              item.account_tx_pushed ||
              isPushing ||
              item?.account_role_for_user === EScanAccountUserRoles.USER ||
              item?.account_role_for_user === EScanAccountUserRoles.SCANNER
            }
          >
            <Typography sx={{ fontSize: 14 }}>
              {t('push-bc-button-text.v', { ns: ETranslationNs.Scans })}
            </Typography>
          </Button>
        </ListItem> */}
      </List>
    </Collapse>
  )
}

export { GridItemCollapse }
